import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import type {
  CreateWarehouseInput,
  UpdateWarehouseInput,
  CreateStockMovementInput,
  CreateStockExitDocumentInput,
  CreateInventoryInput,
  StockMovement,
  StockExitDocument,
  StockVehicleDocumentType,
  Warehouse,
} from '@erp/data-access/interfaces';

const WAREHOUSES_KEY = ['warehouses'] as const;
const STOCK_KEY = ['stock'] as const;
const INVENTORIES_KEY = ['inventories'] as const;
const STOCK_EXITS_KEY = ['stock-exits'] as const;
const OFFLINE_PREFIX = 'OFFLINE-';

export function useWarehouses() {
  const ds = useDataService();
  return useQuery({ queryKey: WAREHOUSES_KEY, queryFn: () => ds.stock.listWarehouses() });
}

export function useCreateWarehouse() {
  const ds = useDataService();
  // Warehouses are infrastructure — created rarely, but still nice to be
  // offline-safe so the user can configure a fresh deployment.
  return useResilientMutation<CreateWarehouseInput, Warehouse>({
    entity: 'stock_movements',
    op: 'custom',
    method: 'createWarehouse',
    invalidate: [WAREHOUSES_KEY],
    online: (input) => ds.stock.createWarehouse(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        code: OFFLINE_PREFIX + clientId.slice(0, 4).toUpperCase(),
        name: input.name,
        isDefault: input.isDefault ?? false,
      }) as Warehouse,
    applyOptimistic: (qc, w) => {
      qc.setQueriesData<Warehouse[]>({ queryKey: WAREHOUSES_KEY }, (old) =>
        Array.isArray(old) && !old.some((x) => x.id === w.id) ? [...old, w] : old,
      );
    },
  });
}

export function useUpdateWarehouse() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateWarehouseInput }, Warehouse>({
    entity: 'stock_movements',
    op: 'custom',
    method: 'updateWarehouse',
    invalidate: [WAREHOUSES_KEY],
    online: ({ id, input }) => ds.stock.updateWarehouse(id, input),
    optimistic: ({ id, input }) =>
      ({
        id,
        code: OFFLINE_PREFIX,
        name: input.name ?? '(édité hors-ligne)',
        isDefault: input.isDefault ?? false,
      }) as Warehouse,
    applyOptimistic: (qc, _r, { id, input }) => {
      qc.setQueriesData<Warehouse[]>({ queryKey: WAREHOUSES_KEY }, (old) =>
        Array.isArray(old) ? old.map((w) => (w.id === id ? { ...w, ...input } : w)) : old,
      );
    },
  });
}

export function useDeleteWarehouse() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'stock_movements',
    op: 'custom',
    method: 'deleteWarehouse',
    invalidate: [WAREHOUSES_KEY],
    online: (id) => ds.stock.deleteWarehouse(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<Warehouse[]>({ queryKey: WAREHOUSES_KEY }, (old) =>
        Array.isArray(old) ? old.filter((w) => w.id !== id) : old,
      );
    },
  });
}

export function useStockMovements(articleId?: string, warehouseId?: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...STOCK_KEY, 'movements', articleId, warehouseId],
    queryFn: () => ds.stock.listMovements(articleId, warehouseId),
  });
}

export function useCreateStockMovement() {
  const ds = useDataService();
  return useResilientMutation<CreateStockMovementInput, StockMovement>({
    entity: 'stock_movements',
    op: 'create',
    invalidate: [STOCK_KEY],
    online: (input) => ds.stock.createMovement(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        articleId: input.articleId,
        warehouseId: input.warehouseId,
        movementType: input.movementType,
        quantity: input.quantity,
        referenceType: null,
      }) as StockMovement,
    applyOptimistic: (qc, mvt) => {
      qc.setQueriesData<StockMovement[]>(
        { queryKey: [...STOCK_KEY, 'movements'] },
        (old) => (Array.isArray(old) ? [mvt, ...old] : old),
      );
    },
  });
}

export function useArticleStock(articleId: string, warehouseId?: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...STOCK_KEY, 'article', articleId, warehouseId],
    queryFn: () => ds.stock.getArticleStock(articleId, warehouseId),
    enabled: !!articleId,
  });
}

export function useAllStock(warehouseId?: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...STOCK_KEY, 'all', warehouseId],
    queryFn: () => ds.stock.getAllStock(warehouseId),
  });
}

export function useInventories(warehouseId?: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...INVENTORIES_KEY, warehouseId],
    queryFn: () => ds.stock.listInventories(warehouseId),
  });
}

export function useInventory(id: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...INVENTORIES_KEY, id],
    queryFn: () => ds.stock.getInventoryById(id),
    enabled: !!id,
  });
}

export function useCreateInventory() {
  const ds = useDataService();
  type Out = { id: string };
  return useResilientMutation<CreateInventoryInput, Out>({
    entity: 'stock_movements',
    op: 'custom',
    method: 'createInventory',
    invalidate: [INVENTORIES_KEY, STOCK_KEY],
    online: (input) => ds.stock.createInventory(input) as Promise<Out>,
    optimistic: (_input, clientId) => ({ id: clientId }),
  });
}

export function useValidateInventory() {
  const ds = useDataService();
  type Out = { id: string };
  return useResilientMutation<string, Out>({
    entity: 'stock_movements',
    op: 'custom',
    method: 'validateInventory',
    invalidate: [INVENTORIES_KEY, STOCK_KEY],
    online: (id) => ds.stock.validateInventory(id) as unknown as Promise<Out>,
    optimistic: (id) => ({ id }),
  });
}

export function useStockExitDocuments(documentType?: StockVehicleDocumentType) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...STOCK_EXITS_KEY, documentType ?? 'all'],
    queryFn: () => ds.stock.listStockExitDocuments(documentType),
  });
}

export function useStockExitDocument(id: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...STOCK_EXITS_KEY, 'detail', id],
    queryFn: () => ds.stock.getStockExitDocumentById(id),
    enabled: !!id,
  });
}

export function useCreateStockExitDocument() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useResilientMutation<CreateStockExitDocumentInput, StockExitDocument>({
    entity: 'stock_exit_documents',
    op: 'create',
    invalidate: [STOCK_EXITS_KEY],
    online: (input) => ds.stock.createStockExitDocument(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        documentType: input.documentType,
        number: OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase(),
        documentDate: input.documentDate,
        warehouseId: input.warehouseId,
        lines: input.lines,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }) as unknown as StockExitDocument,
    applyOptimistic: (qc, doc) => {
      qc.setQueryData([...STOCK_EXITS_KEY, 'detail', doc.id], doc);
      qc.setQueriesData<StockExitDocument[]>({ queryKey: STOCK_EXITS_KEY }, (old) =>
        Array.isArray(old) && !old.some((d) => d.id === doc.id) ? [doc, ...old] : old,
      );
    },
  });
}
